
import { Menu as VMenu } from 'floating-vue'
import { defineComponent, PropType } from 'vue'

import type { IgnoreContext } from '@/components/RepoIssues/IssueIgnoreDropdown.vue'

import {
  Analyzer,
  IgnoreCheckIssueActionChoice,
  IssueSeverity,
  VcsProviderChoices
} from '~/types/types'
import { UiIssueLevel } from '~/utils/issue'
import { createPopperScrollGuard, PopperScrollGuard } from '~/utils/popper'
import { safeRenderBackticks } from '~/utils/string'
import {
  getIssueSeverityIcon,
  getIssueSeverityIconColor,
  getIssueSeverityLabel,
  getIssueTypeIcon,
  getIssueTypeLabel
} from '~/utils/ui'

export default defineComponent({
  name: 'IssueEditor',
  components: {
    VMenu
  },
  props: {
    id: {
      type: String
    },
    path: {
      type: String
    },
    text: {
      type: String
    },
    sourceCodeMarkup: {
      type: String
    },
    beginLine: {
      type: Number,
      required: true
    },
    endLine: {
      type: Number,
      required: true
    },
    blobUrlRoot: {
      type: String,
      required: true
    },
    checkIssueIds: {
      type: Array as PropType<Array<string>>,
      default: () => []
    },
    checkId: {
      type: String
    },
    shortcode: {
      type: String,
      default: ''
    },
    canIgnoreIssues: {
      type: Boolean,
      default: false
    },
    snippetsLoading: {
      type: Boolean,
      default: false
    },
    snippetsFetchErrored: {
      type: Boolean,
      default: false
    },
    isSubrepo: {
      type: Boolean,
      default: false
    },
    repoPath: {
      type: String,
      default: ''
    },
    disableFp: {
      type: Boolean,
      default: false
    },
    ignoreActionType: {
      type: String as PropType<IgnoreCheckIssueActionChoice>,
      required: true
    },
    issueLevel: {
      type: String as PropType<UiIssueLevel>,
      default: UiIssueLevel.Repository
    },
    category: {
      type: String,
      default: ''
    },
    isAiReview: {
      type: Boolean,
      default: false
    },
    severity: {
      type: String as PropType<IssueSeverity>,
      default: ''
    },
    analyzer: {
      type: Object as PropType<Analyzer>,
      default: null
    },
    shortExplanation: {
      type: String,
      default: ''
    },
    explanation: {
      type: String,
      default: ''
    },
    runId: {
      type: String,
      default: ''
    },
    forceCloseIgnoreMenu: {
      type: Boolean,
      default: false
    },
    context: {
      type: String as PropType<IgnoreContext>,
      default: 'issues-tab'
    },
    repositoryId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isOpen: false,
      currentComponent: '',
      isIgnored: false,
      isExpanded: true,
      isExplainOpen: false,
      explainPopoverGuard: null as PopperScrollGuard | null
    }
  },
  created(): void {
    this.explainPopoverGuard = createPopperScrollGuard(
      () => {
        this.isExplainOpen = false
      },
      {
        useInitGuard: true
      }
    )
  },
  methods: {
    safeRenderBackticks,
    toggleExpanded(): void {
      this.isExpanded = !this.isExpanded
    },
    close(): void {
      this.isOpen = false
    },
    handleIgnoreFromDropdown(payload: { ignoredIssueIds: string[]; checkId?: string }): void {
      this.markOccurrencesDisabled(payload.ignoredIssueIds)
    },
    onIgnoreMenuToggled(payload: { id: string; isOpen: boolean }): void {
      this.$emit('ignoreMenuToggled', payload)
    },
    openIgnoreAiReviewModal(): void {
      if (!this.id) return
      this.currentComponent = 'aiReview'
      this.isOpen = true
    },
    markOccurrencesDisabled(checkIssueIds: Array<string>): void {
      this.isIgnored = true
      this.close()

      // Emit new event for local state update (used by analyzer page)
      this.$emit('issueIgnored', {
        ignoredIssueIds: checkIssueIds,
        checkId: this.checkId
      })

      // Keep existing events for backward compatibility with other pages
      if (this.runId) {
        this.$root.$emit('refetchRunAndCheck', { run_id: this.runId, check_id: this.checkId })
      } else {
        this.$root.$emit('refetchCheck', this.checkId)
      }
      this.$emit('ignoreIssues', checkIssueIds)
    },
    /**
     * Format category from API format (BUG_RISK) to UI format (bug-risk)
     */
    formatCategory(category: string): string {
      return category.toLowerCase().replace(/_/g, '-')
    },
    /**
     * Get icon name for issue category
     */
    getCategoryIcon(category: string): string {
      return getIssueTypeIcon(this.formatCategory(category))
    },
    /**
     * Get display label for issue category
     */
    getCategoryLabel(category: string): string {
      return getIssueTypeLabel(this.formatCategory(category))
    },
    /**
     * Handle explain popover show - add scroll listener to close on outside scroll
     */
    onExplainShow(): void {
      this.explainPopoverGuard?.onShow()
    },
    /**
     * Handle explain popover hide - remove scroll listener
     */
    onExplainHide(): void {
      this.explainPopoverGuard?.onHide()
    }
  },
  beforeDestroy(): void {
    // Clean up scroll listeners in case component is destroyed while popovers are open
    this.explainPopoverGuard?.cleanup()
  },
  computed: {
    severityIcon(): string {
      return this.severity ? getIssueSeverityIcon(this.severity as IssueSeverity) : ''
    },
    severityIconColor(): string {
      return this.severity
        ? getIssueSeverityIconColor(this.severity as IssueSeverity)
        : 'vanilla-400'
    },
    severityLabel(): string {
      return this.severity ? getIssueSeverityLabel(this.severity as IssueSeverity) : ''
    },
    directoryUrl(): string {
      if (!this.shortcode || !this.analyzer?.shortcode) return ''
      const language = this.analyzer.shortcode.toLowerCase()
      return `${this.$config.directoryBaseUrl}/${language}/issues/${this.shortcode}`
    },
    blobUrl(): string {
      const basePath = this.isSubrepo ? `${this.blobUrlRoot}${this.repoPath}/` : this.blobUrlRoot
      const provider = this.$providerMetaMap[this.$route.params.provider].value

      if (provider === VcsProviderChoices.Github) {
        return `${basePath}${this.path}#L${this.beginLine}-L${this.endLine}`
      }
      if (provider === VcsProviderChoices.Gitlab) {
        return `${basePath}${this.path}#L${this.beginLine}-${this.endLine}`
      }
      if (provider === VcsProviderChoices.Bitbucket) {
        return `${basePath}${this.path}#lines-${this.beginLine}:${this.endLine}`
      }
      return ''
    }
  }
})
